import React, { useState, useImperativeHandle, forwardRef } from "react";
import { Link } from "react-router-dom";

interface TermsAndPrivacyProps {
	/* checked - is the value of the checkbox (starting value) */
	checked: boolean;

	/* onCheckboxChange - function that is called when the checkbox changes state */
	onCheckboxChange: (checked: boolean) => void;

	onValidate?: (checked: boolean) => boolean;

	/* termsUrl - optional url to connect the terms (defaults: /terms)*/
	termsUrl?: string;

	/* privacyUrl - optional url to connect the privacy (defaults: /privacy)*/
	privacyUrl?: string;

	/* onTermsClick - optional function that is called when the terms is clicked */
	onTermsClick?: () => void;

	/* onPrivacyClick - optional function that is called when the privacy is clicked */
	onPrivacyClick?: () => void;
	validationText?: string;
}

export interface TermsAndPrivacyRef {
	triggerValidation: () => boolean;
}

const TermsAndPrivacy = forwardRef<TermsAndPrivacyRef, TermsAndPrivacyProps>(
	(
		{
			checked,
			onCheckboxChange,
			onValidate = (checked: boolean) => checked,
			termsUrl = "/terms",
			privacyUrl = "/privacy",
			validationText = "Please accept the terms to continue."
		},
		ref
	) => {
		const [isChecked, setIsChecked] = useState(checked);
		const [isValid, setIsValid] = useState(true);
		const [isFocused, setIsFocused] = useState<boolean>(false);

		// Handle checkbox change
		const handleCheckboxChange = (): void => {
			setIsChecked(!isChecked);
			onCheckboxChange(!isChecked);
			setIsValid(onValidate?.(!isChecked));
		};

		const handleFocus = (): void => {
			setIsFocused(true);
		};

		const handleBlur = (): void => {
			setIsFocused(false);
		};

		useImperativeHandle(ref, () => ({
			triggerValidation: (): boolean => {
				const valid = onValidate(checked);
				setIsValid(valid);
				return valid;
			}
		}));

		return (
			<div
				className="flex flex-col justify-center px-4 py-3"
				aria-label="Terms and Privacy agreement"
			>
				<div>
					<span className="flex items-center justify-center">
						<input
							type="checkbox"
							id="terms-privacy-checkbox"
							checked={checked}
							onChange={handleCheckboxChange}
							onFocus={handleFocus}
							onBlur={handleBlur}
							className="sr-only peer"
						/>
						<label
							htmlFor="terms-privacy-checkbox"
							className={`block w-6 h-6 border-2 border-white rounded cursor-pointer ${
								isChecked ? "bg-primary-active" : "bg-primary-background"
							} peer-focus:ring-1 peer-focus:ring-offset-2 peer-focus:ring-offset-primary-background peer-focus:ring-primary-active transition-all`}
						>
							<label
								htmlFor="terms-privacy-checkbox"
								className={`relative cursor-pointer select-none text-xl font-bold text-black left-0.5 -top-1 ${
									isChecked ? "" : "hidden"
								}`}
							>
								✓
							</label>
						</label>

						<label
							htmlFor="terms-privacy-checkbox"
							className="text-sm ml-3 text-primary-active cursor-pointer select-none"
						>
							I agree to the
							<Link
								to={termsUrl}
								target="_blank"
								//onClick={onTermsClick}
								className="text-sm ml-1 mr-1 font-bold underline"
							>
								Terms of Service
							</Link>
							<label
								htmlFor="terms-privacy-checkbox"
								className="text-sm cursor-pointer select-none"
							>
								and
							</label>
							<Link
								to={privacyUrl}
								//onClick={onPrivacyClick}
								target="_blank"
								className="text-sm ml-1 mr-1 font-bold underline"
							>
								Privacy Policy
							</Link>
						</label>
					</span>
				</div>

				<div
					onClick={handleCheckboxChange}
					className={`cursor-default pl-16 text-sm mt-1 transition-opacity duration-200 min-h-6 ${
						isValid ? "hidden" : ""
					} ${isFocused ? "text-error-active" : "text-error-inactive"}`}
				>
					{validationText}
				</div>
			</div>
		);
	}
);

TermsAndPrivacy.displayName = "TermsAndPrivacy";

export default TermsAndPrivacy;
